import { FormEvent, useEffect, useState } from 'react';
import EmptyState from '@/components/common/EmptyState';
import FormMessage from '@/components/common/FormMessage';
import { roleLabel } from '@/lib/role';
import { getUserFriendlyMessage } from '@/services/apiError';
import { updateMyProfile } from '@/services/profileApi';
import { useAuthStore } from '@/store/authStore';

export default function ProfileSettingsPage() {
  const user = useAuthStore(state => state.user);
  const updateUser = useAuthStore(state => state.updateUser);
  const [username, setUsername] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const canSubmit =
    Boolean(user) &&
    Boolean(username.trim()) &&
    Boolean(email.trim()) &&
    !saving &&
    (username.trim() !== (user?.name ?? '') || email.trim() !== (user?.email ?? ''));

  useEffect(() => {
    setUsername(user?.name ?? '');
    setEmail(user?.email ?? '');
  }, [user?.id, user?.name, user?.email]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const profile = await updateMyProfile({
        name: username.trim(),
        email: email.trim(),
      });
      updateUser({
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: profile.role,
        createdAt: toIsoString(profile.createdAt),
      });
      setSuccess('个人资料已保存');
    } catch (unknownError) {
      setError(getUserFriendlyMessage(unknownError, '保存失败，请稍后重试'));
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return <EmptyState title="未找到用户信息" description="请重新登录后更新个人资料。" />;
  }

  return (
    <section className="max-w-[640px] bg-white rounded-2xl p-6 sm:p-8 shadow-card-sm space-y-6">
      <div>
        <h2 className="font-semibold text-apple-near-black text-[1.3125rem] leading-tight">
          个人资料设置
        </h2>
        <p className="mt-1 text-[0.875rem] text-apple-gray">
          这些信息会显示在你的账户和导航菜单中。
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl border border-apple-border bg-apple-light-gray p-4">
        <div>
          <p className="text-[0.75rem] text-apple-gray">当前身份</p>
          <p className="mt-1 text-[0.9375rem] text-apple-near-black">{roleLabel(user.role)}</p>
        </div>
        <div>
          <p className="text-[0.75rem] text-apple-gray">注册时间</p>
          <p className="mt-1 text-[0.9375rem] text-apple-near-black">
            {user.createdAt ? new Date(user.createdAt).toLocaleDateString('zh-CN') : '-'}
          </p>
        </div>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-1.5">
          <label
            className="block text-[0.875rem] font-medium text-apple-near-black"
            htmlFor="profile-username"
          >
            用户名
          </label>
          <input
            id="profile-username"
            className="input"
            value={username}
            disabled={saving}
            onChange={event => setUsername(event.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <label
            className="block text-[0.875rem] font-medium text-apple-near-black"
            htmlFor="profile-email"
          >
            邮箱
          </label>
          <input
            id="profile-email"
            type="email"
            className="input"
            value={email}
            disabled={saving}
            onChange={event => setEmail(event.target.value)}
          />
        </div>

        {error ? <FormMessage tone="error" message={error} /> : null}
        {success ? <FormMessage tone="success" message={success} /> : null}

        <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
          {saving ? '保存中...' : '保存资料'}
        </button>
      </form>
    </section>
  );
}

function toIsoString(value: string | Date) {
  if (typeof value === 'string') {
    return value;
  }
  return value.toISOString();
}
